import React from "react";
import { Head } from "@inertiajs/react";
import { Grid, Stack, Group, Button, Anchor } from "@mantine/core";
import PageShell from "../Components/Layout/PageShell";
import { Heading1, Heading2, Heading3, DimmedText, MainText } from "../Components/UI/Typography";
import { Card } from "../Components/UI/Card";
import { Input, Textarea } from "../Components/UI/Forms";
import { useEmailForm } from "../src/hooks/useEmailForm";
import {
  IconBrandLinkedin,
  IconCalendar,
  IconMail,
} from "@tabler/icons-react";

export default function Contact() {
  const { form, handleChange, submit, loading } = useEmailForm();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit();
  };

  return (
    <>
      <Head>
        <title>Contact | Ogarocious</title>
        <meta name="description" content="Get in touch with Ogarocious. Send a message, schedule a call, or connect on LinkedIn to start a conversation." />
        <meta property="og:title" content="Contact | Ogarocious" />
        <meta property="og:description" content="Get in touch with Ogarocious. Send a message, schedule a call, or connect on LinkedIn." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL || ""}/contact`} />
        <meta property="og:image" content={import.meta.env.VITE_OG_IMAGE_URL || ""} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact | Ogarocious" />
        <meta name="twitter:description" content="Get in touch with Ogarocious. Send a message, schedule a call, or connect on LinkedIn." />
        <meta name="twitter:image" content={import.meta.env.VITE_OG_IMAGE_URL || ""} />
      </Head>
      <PageShell variant="content">
        <Stack gap={60}>
            {/* Header */}
            <Stack gap="md" align="center">
              <Heading1 variant="shadow">Get In Touch</Heading1>
              <DimmedText size="lg" style={{ textAlign: "center", maxWidth: "clamp(280px, 90vw, 600px)" }}>
                Have a project in mind, want to collaborate, or just want to say hello?
                I&apos;d love to hear from you.
              </DimmedText>
            </Stack>

            {/* Two-column layout */}
            <Grid gutter="xl">
              {/* Left: Contact Form */}
              <Grid.Col span={{ base: 12, md: 7 }}>
                <Card padding="xl" radius="md">
                  <form onSubmit={handleSubmit}>
                    <Stack gap="md">
                      <Heading2>Send a Message</Heading2>
                      <Grid>
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                          <Input
                            label="Name"
                            placeholder="Your name"
                            value={form.name}
                            onChange={(e) =>
                              handleChange("name", e.currentTarget.value)
                            }
                            required
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                          <Input
                            label="Email"
                            placeholder="you@example.com"
                            type="email"
                            value={form.email}
                            onChange={(e) =>
                              handleChange("email", e.currentTarget.value)
                            }
                            required
                          />
                        </Grid.Col>
                      </Grid>
                      <Input
                        label="Subject"
                        placeholder="What's this about?"
                        value={form.subject}
                        onChange={(e) =>
                          handleChange("subject", e.currentTarget.value)
                        }
                        required
                      />
                      <Textarea
                        label="Message"
                        placeholder="Tell me about your project or idea..."
                        minRows={5}
                        value={form.message}
                        onChange={(e) =>
                          handleChange("message", e.currentTarget.value)
                        }
                        required
                      />
                      <Group justify="flex-end">
                        <Button
                          type="submit"
                          variant="gradient"
                          size="md"
                          loading={loading}
                        >
                          Send Message
                        </Button>
                      </Group>
                    </Stack>
                  </form>
                </Card>
              </Grid.Col>

              {/* Right: Alternative contact methods */}
              <Grid.Col span={{ base: 12, md: 5 }}>
                <Stack gap="md">
                  <Card padding="xl" radius="md">
                    <Stack gap="md">
                      <Group gap="sm">
                        <IconBrandLinkedin size={24} style={{ color: "var(--mantine-color-orange-6)" }} />
                        <Heading3>Connect on LinkedIn</Heading3>
                      </Group>
                      <DimmedText>
                        Let&apos;s connect professionally. I&apos;m always open to new opportunities and conversations.
                      </DimmedText>
                      <Anchor
                        href="https://linkedin.com/in/ogarocious"
                        target="_blank"
                        underline="never"
                      >
                        <Button variant="outline" fullWidth>
                          View LinkedIn Profile
                        </Button>
                      </Anchor>
                    </Stack>
                  </Card>

                  <Card padding="xl" radius="md">
                    <Stack gap="md">
                      <Group gap="sm">
                        <IconCalendar size={24} style={{ color: "var(--mantine-color-orange-6)" }} />
                        <Heading3>Schedule a Call</Heading3>
                      </Group>
                      <DimmedText>
                        Prefer to talk it through? Book a time that works for you.
                      </DimmedText>
                      <Anchor
                        href="https://cal.com/ogarocious"
                        target="_blank"
                        underline="never"
                      >
                        <Button variant="outline" fullWidth>
                          Book a Time
                        </Button>
                      </Anchor>
                    </Stack>
                  </Card>

                  <Card padding="xl" radius="md">
                    <Stack gap="md">
                      <Group gap="sm">
                        <IconMail size={24} style={{ color: "var(--mantine-color-orange-6)" }} />
                        <Heading3>Email Directly</Heading3>
                      </Group>
                      <MainText>
                        hello@ogarocious.com
                      </MainText>
                    </Stack>
                  </Card>
                </Stack>
              </Grid.Col>
            </Grid>
        </Stack>
      </PageShell>
    </>
  );
}
